import { type Dispatch, type SetStateAction, useCallback, useMemo, useState } from 'react';
import type { Node, NodeMouseHandler } from '@xyflow/react';
import { GraphNavDirection } from '../Cmarkdowngraphcontrols';
import { FlowchartHoverState } from './flowchart-context';
import {
  FLOWCHART_DIRECTION_KEY,
  type FlowchartDirectionKey,
  buildUndirectedNeighborMap,
  findDirectionalNavigationTarget,
  getNodeNeighborhood,
} from './flowchart-interactions';
import type { FlowchartNodeData } from './flowchart-types';
import type { LayoutResult } from './layout-flowchart';

const GROUP_NODE_TYPE = 'flowchartGroup';

const NAV_DIRECTION_TO_KEY: Record<GraphNavDirection, FlowchartDirectionKey> = {
  [GraphNavDirection.Up]: FLOWCHART_DIRECTION_KEY.Up,
  [GraphNavDirection.Down]: FLOWCHART_DIRECTION_KEY.Down,
  [GraphNavDirection.Left]: FLOWCHART_DIRECTION_KEY.Left,
  [GraphNavDirection.Right]: FLOWCHART_DIRECTION_KEY.Right,
};

interface UseFlowchartInteractionsInput {
  centerCameraOnNode: (nodeId: string) => void;
  focusedNodeId: string | null;
  layout: LayoutResult | null;
  setFocusedNodeId: Dispatch<SetStateAction<string | null>>;
}

interface UseFlowchartInteractionsResult {
  focusedNodeId: string | null;
  focusNodeNeighborhood: (nodeId: string) => void;
  handleDirectionalNavigation: (direction: FlowchartDirectionKey) => void;
  handleNavigate: (direction: GraphNavDirection) => void;
  handlePaneClick: () => void;
  hoveredNodeId: string | null;
  interactionState: FlowchartHoverState;
  onNodeClick: NodeMouseHandler<Node<FlowchartNodeData>>;
  onNodeMouseEnter: NodeMouseHandler<Node<FlowchartNodeData>>;
  onNodeMouseLeave: NodeMouseHandler<Node<FlowchartNodeData>>;
  setFocusedNodeId: (nodeId: string | null) => void;
}

function isInteractiveFlowchartNode(node: Node<FlowchartNodeData>): boolean {
  return node.type !== GROUP_NODE_TYPE;
}

export function useFlowchartInteractions({
  centerCameraOnNode,
  focusedNodeId,
  layout,
  setFocusedNodeId,
}: UseFlowchartInteractionsInput): UseFlowchartInteractionsResult {
  const [hoveredNodeId, setHoveredNodeId] = useState<string | null>(null);

  const neighborMap = useMemo(() => {
    if (!layout) {
      return new Map<string, Set<string>>();
    }

    return buildUndirectedNeighborMap(layout.edges);
  }, [layout]);

  const hoveredConnectedNodeIds = useMemo(() => {
    if (!hoveredNodeId) {
      return new Set<string>();
    }

    return getNodeNeighborhood({
      nodeId: hoveredNodeId,
      neighborMap,
    });
  }, [hoveredNodeId, neighborMap]);

  const focusedNodeIds = useMemo(() => {
    if (!focusedNodeId) {
      return new Set<string>();
    }

    return getNodeNeighborhood({
      nodeId: focusedNodeId,
      neighborMap,
    });
  }, [focusedNodeId, neighborMap]);

  const interactionState = useMemo<FlowchartHoverState>(() => {
    return {
      hoveredNodeId,
      hoveredConnectedNodeIds,
      focusedNodeId,
      focusedNodeIds,
    };
  }, [focusedNodeId, focusedNodeIds, hoveredConnectedNodeIds, hoveredNodeId]);

  const focusNodeNeighborhood = useCallback((nodeId: string) => {
    setFocusedNodeId(nodeId);
    centerCameraOnNode(nodeId);
  }, [centerCameraOnNode, setFocusedNodeId]);

  const handleDirectionalNavigation = useCallback((direction: FlowchartDirectionKey) => {
    const targetNodeId = findDirectionalNavigationTarget({
      nodes: layout?.nodes ?? [],
      activeNodeId: focusedNodeId ?? hoveredNodeId,
      direction,
    });

    if (!targetNodeId) {
      return;
    }

    focusNodeNeighborhood(targetNodeId);
  }, [focusedNodeId, focusNodeNeighborhood, hoveredNodeId, layout]);

  const handleNavigate = useCallback((direction: GraphNavDirection) => {
    handleDirectionalNavigation(NAV_DIRECTION_TO_KEY[direction]);
  }, [handleDirectionalNavigation]);

  const onNodeMouseEnter: NodeMouseHandler<Node<FlowchartNodeData>> = useCallback(
    (_event, node) => {
      if (!isInteractiveFlowchartNode(node)) {
        return;
      }
      setHoveredNodeId(node.id);
    },
    [],
  );

  const onNodeMouseLeave: NodeMouseHandler<Node<FlowchartNodeData>> = useCallback(
    () => setHoveredNodeId(null),
    [],
  );

  const onNodeClick: NodeMouseHandler<Node<FlowchartNodeData>> = useCallback(
    (_event, node) => {
      if (!isInteractiveFlowchartNode(node)) {
        return;
      }
      focusNodeNeighborhood(node.id);
    },
    [focusNodeNeighborhood],
  );

  const handlePaneClick = useCallback(() => {
    setFocusedNodeId(null);
  }, [setFocusedNodeId]);

  return {
    focusedNodeId,
    focusNodeNeighborhood,
    handleDirectionalNavigation,
    handleNavigate,
    handlePaneClick,
    hoveredNodeId,
    interactionState,
    onNodeClick,
    onNodeMouseEnter,
    onNodeMouseLeave,
    setFocusedNodeId,
  };
}
